import { formatSignedPercent } from '../core/utils';

export type FundingRateLegendMarker = 'solid' | 'dashed';

/** How a series' single legend number is derived from its data points. */
export type FundingRateLegendValueMode =
  /** Latest data point's value (used for funding-rate and moving-average lines, already in %). */
  | 'latest'
  /** Percent change from the first to the last point (used for the price line, whose axis is USD). */
  | 'change';

export interface FundingRateLegendSeriesInput {
  /** Stable key for the chip. */
  id: string;
  /** Display label (e.g. exchange name, 'Aggregated Funding Rate', 'BTC'). */
  label: string;
  data: [number, number][];
  color: string;
  marker: FundingRateLegendMarker;
  valueMode: FundingRateLegendValueMode;
}

export interface FundingRateLegendItem {
  id: string;
  label: string;
  /** Pre-formatted signed percent (e.g. '+0.4872%'), or empty when there is no data. */
  value: string;
  color: string;
  isPositive: boolean;
  marker: FundingRateLegendMarker;
}

/** Percent change from the first to the last point; null when it cannot be computed. */
function percentChange(data: [number, number][]): number | null {
  if (data.length < 2) {
    return null;
  }
  const first = data[0][1];
  const last = data[data.length - 1][1];
  if (!Number.isFinite(first) || !Number.isFinite(last) || first === 0) {
    return null;
  }
  return ((last - first) / first) * 100;
}

/** Last point's value; null when the series is empty. */
function latestValue(data: [number, number][]): number | null {
  if (data.length === 0) {
    return null;
  }
  const last = data[data.length - 1][1];
  return Number.isFinite(last) ? last : null;
}

/**
 * Builds the legend items shown over the funding rate chart. Each series contributes one chip;
 * series with no usable data are dropped so the legend never shows an empty or NaN value.
 */
export function buildFundingRateLegendItems(inputs: FundingRateLegendSeriesInput[]): FundingRateLegendItem[] {
  const items: FundingRateLegendItem[] = [];
  for (const input of inputs) {
    const raw = input.valueMode === 'change' ? percentChange(input.data) : latestValue(input.data);
    if (raw === null) {
      continue;
    }
    items.push({
      id: input.id,
      label: input.label,
      value: formatSignedPercent(raw),
      color: input.color,
      isPositive: raw >= 0,
      marker: input.marker,
    });
  }
  return items;
}
